// Which mortgage statements are on file, and which due dates are missing.
// See ADR-0012.
//
// Mortgages are simpler than utility bills (billCoverage.js): the lender sets a
// fixed monthly due date from the loan's first payment, so "missing" is just
// the expected due months minus the months that have a statement. There is no
// billing-cycle drift and no service-period overlap to reason about.
//
// Dates go through toUtcDay/monthIndexOfDay like every other bill surface
// (CLAUDE.md "Bill Period Math"): the mortgage API sends ISO dates today, but
// nothing here should depend on that.

import { firstPaymentDate, mortgageMaturityDate } from './dateHelpers';
import { MONTH_LABELS, monthIndexOfDay, monthName, toUtcDay } from './billCoverage';

const monthIndexOf = (value) => {
  const day = toUtcDay(value);
  return day == null ? null : monthIndexOfDay(day);
};

const createdAtMs = (statement) => {
  const ms = Date.parse(statement.created_at || '');
  return Number.isNaN(ms) ? -Infinity : ms;
};

const descending = (x, y) => (x === y ? 0 : x < y ? 1 : -1);

/**
 * Newest due date first, undated last, ties by newest created_at — the same
 * order as list_statements' ORDER BY, so a statement merged in client-side
 * lands where a reload would put it.
 */
export const sortStatementsNewestFirst = (statements) =>
  [...statements].sort(
    (a, b) =>
      descending(toUtcDay(a.due_date) ?? -Infinity, toUtcDay(b.due_date) ?? -Infinity) ||
      descending(createdAtMs(a), createdAtMs(b)),
  );

/**
 * Build the statement coverage grid for a loan.
 *
 * The first expected due month is the loan's first payment, derived from its
 * origination date by the same rule the amortization schedule uses
 * (firstPaymentDate). When a statement predates that estimate, the grid starts
 * at the statement instead: the rule is a convention, the statement is a fact.
 * Without an origination date the grid starts at the earliest statement.
 *
 * Every due month up to and including the current month is expected. The next
 * due month is "next" (its statement usually arrives a couple of weeks early),
 * and nothing after it is shown. For a loan that hasn't reached its first
 * payment yet, the next due month IS the first payment. Due months past the
 * loan's maturity are not expected.
 *
 * Cell status: received | missing | next | before | after | future.
 *
 * @param {Array} statements - statements as returned by the API, any order.
 * @param {{originationDate?: string, maturityDate?: string, termMonths?: number, today?: Date}} [loan]
 */
export const buildStatementCoverage = (
  statements = [],
  { originationDate, maturityDate, termMonths, today = new Date() } = {},
) => {
  const currentMonthIndex = today.getFullYear() * 12 + today.getMonth();

  const byMonth = new Map();
  let undatedCount = 0;
  for (const statement of sortStatementsNewestFirst(statements)) {
    const monthIndex = monthIndexOf(statement.due_date);
    if (monthIndex == null) {
      undatedCount += 1;
      continue;
    }
    if (!byMonth.has(monthIndex)) byMonth.set(monthIndex, []);
    byMonth.get(monthIndex).push(statement);
  }
  const datedMonths = [...byMonth.keys()];

  const scheduledFirst = monthIndexOf(firstPaymentDate(originationDate));
  const earliest = datedMonths.length ? Math.min(...datedMonths) : null;
  const firstMonthIndex =
    scheduledFirst == null ? earliest : earliest == null ? scheduledFirst : Math.min(scheduledFirst, earliest);

  const empty = {
    source: null,
    firstMonthIndex: null,
    expectedCount: 0,
    onFileCount: 0,
    missingCount: 0,
    missingRanges: [],
    duplicates: [],
    beyondSchedule: [],
    undatedCount,
    nextMonthIndex: null,
    years: [],
  };
  if (firstMonthIndex == null) return empty;

  const maturityMonthIndex = monthIndexOf(maturityDate || mortgageMaturityDate(originationDate, termMonths));
  const lastExpected =
    maturityMonthIndex == null ? currentMonthIndex : Math.min(currentMonthIndex, maturityMonthIndex);
  // Next calendar month, unless the first payment is still further out (a loan
  // originated this month, or in the future).
  const upcoming =
    scheduledFirst != null && scheduledFirst > currentMonthIndex + 1 ? scheduledFirst : currentMonthIndex + 1;
  const nextMonthIndex = maturityMonthIndex == null || upcoming <= maturityMonthIndex ? upcoming : null;
  // The grid never grows past the next due month: a mistyped 2062 statement is
  // reported, not given 36 rows of empty future.
  const gridEnd = nextMonthIndex ?? lastExpected;

  const afterMaturity = (monthIndex) => maturityMonthIndex != null && monthIndex > maturityMonthIndex;
  const statusOf = (monthIndex, onFile) => {
    if (monthIndex > gridEnd) return afterMaturity(monthIndex) ? 'after' : 'future';
    if (onFile.length) return 'received';
    if (monthIndex < firstMonthIndex) return 'before';
    if (afterMaturity(monthIndex)) return 'after';
    if (monthIndex <= lastExpected) return 'missing';
    return monthIndex === nextMonthIndex ? 'next' : 'future';
  };

  const years = [];
  for (let year = Math.floor(firstMonthIndex / 12); year <= Math.floor(gridEnd / 12); year += 1) {
    const months = MONTH_LABELS.map((label, month) => {
      const monthIndex = year * 12 + month;
      const onFile = monthIndex > gridEnd ? [] : byMonth.get(monthIndex) || [];
      return {
        monthIndex,
        label,
        longLabel: monthName(monthIndex, 'long'),
        status: statusOf(monthIndex, onFile),
        statements: onFile,
        // Newest first, so a corrected re-issue shows over the original.
        amount: onFile.length ? onFile[0].total_amount_due ?? null : null,
        isDuplicate: onFile.length > 1,
      };
    });
    years.push({ year, months });
  }

  const missingRanges = [];
  let onFileCount = 0;
  for (let monthIndex = firstMonthIndex; monthIndex <= lastExpected; monthIndex += 1) {
    if (byMonth.has(monthIndex)) {
      onFileCount += 1;
      continue;
    }
    const last = missingRanges[missingRanges.length - 1];
    if (last && last.endMonthIndex === monthIndex - 1) {
      last.endMonthIndex = monthIndex;
      last.count += 1;
    } else {
      missingRanges.push({ startMonthIndex: monthIndex, endMonthIndex: monthIndex, count: 1 });
    }
  }
  const expectedCount = Math.max(0, lastExpected - firstMonthIndex + 1);

  return {
    source: scheduledFirst == null ? 'statements' : 'origination',
    firstMonthIndex,
    expectedCount,
    onFileCount,
    missingCount: expectedCount - onFileCount,
    missingRanges,
    duplicates: datedMonths
      .filter((monthIndex) => byMonth.get(monthIndex).length > 1 && monthIndex <= gridEnd)
      .sort((a, b) => a - b)
      .map((monthIndex) => ({ monthIndex, statements: byMonth.get(monthIndex) })),
    beyondSchedule: datedMonths
      .filter((monthIndex) => monthIndex > gridEnd)
      .sort((a, b) => a - b)
      .flatMap((monthIndex) => byMonth.get(monthIndex)),
    undatedCount,
    nextMonthIndex,
    years,
  };
};
